import * as React from "react";
import { IExistingGcp } from "../../../apiFacades/bapi/gcpRequests";
import { ExistingGcpItem } from "./ExistingGcpItem";
import {
  Alert,
  TextItem,
  Gray100,
  Gray20,
  typeCompact12Medium,
  typeCompact14Regular,
} from "@traceair/tds";
import { faintBlue } from "../../Generics/_colors";
import { classes, stylesheet } from "typestyle";
import ExpandMoreIcon from "../components/CommentForPilot/ExpandMoreIcon";
import ExpandLessIcon from "../components/CommentForPilot/ExpandLessIcon";
import { GcpSidebarIcon } from "../../../redux/computables/flightMarkerIcon/GcpSidebarIcon";

const css = stylesheet({
  notice: {
    padding: "6px 12px",
  },
  alertText: {
    color: Gray100,
  },
  text: {
    color: Gray100,
    marginBottom: 10,
  },
  listHidden: {
    display: "none",
  },
  expandButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#055FC2",
    height: 32,
  },
});

interface IControlProps {
  existingGcp: IExistingGcp;
  active?: boolean;
  onClick: () => void;
}

export const ControlItem = (props: IControlProps) => {
  const { existingGcp, active, onClick } = props;

  return (
    <ExistingGcpItem
      leftIcon={
        <GcpSidebarIcon
          type="existing"
          isExistingHighPrio={existingGcp.highPriority}
          isSelected={active}
        />
      }
      leftLabel={existingGcp.name}
      description={existingGcp.measurementGuideComment}
      backgroundColor={active ? faintBlue : "#FFFFFF"}
      borderBottomColor={Gray20}
      onClick={onClick}
    />
  );
};

interface IProps {
  existingGcps: IExistingGcp[];
  activeGcpId: string;
  onExistingGcpClick: (existingGcpId: string) => void;
}

export const ExistingControlList = (props: IProps) => {
  const [open, setOpen] = React.useState(false);
  const { existingGcps, activeGcpId, onExistingGcpClick } = props;
  if (!existingGcps.length) {
    return null;
  }

  const highPrio = existingGcps.filter((gcp) => gcp.highPriority);
  const lowPrio = existingGcps.filter((gcp) => !gcp.highPriority);

  const hasPriority = existingGcps.some((gcp) => gcp.highPriority);

  return (
    <div>
      <div className={css.notice}>
        <TextItem
          style={classes(css.text, typeCompact12Medium)}
          text="Find at least 5 existing controls on site and measure coordinates."
        />
        {hasPriority && (
          <Alert
            type="neutral"
            description={
              <TextItem
                style={classes(css.alertText, typeCompact12Medium)}
                text="Check dark blue markers first."
              />
            }
          />
        )}
      </div>
      <div>
        {highPrio.map((existingGcp) => {
          return (
            <ControlItem
              key={existingGcp.id}
              existingGcp={existingGcp}
              onClick={() => onExistingGcpClick(existingGcp.id)}
              active={existingGcp.id === activeGcpId}
            />
          );
        })}
        <div className={classes(hasPriority && !open && css.listHidden)}>
          {lowPrio.map((existingGcp) => {
            return (
              <ControlItem
                key={existingGcp.id}
                existingGcp={existingGcp}
                onClick={() => onExistingGcpClick(existingGcp.id)}
                active={existingGcp.id === activeGcpId}
              />
            );
          })}
        </div>
      </div>
      {hasPriority && (
        <div className={css.expandButton} onClick={() => setOpen((s) => !s)}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          <TextItem
            style={typeCompact14Regular}
            text={open ? "Fewer controls" : "More controls"}
          />
        </div>
      )}
    </div>
  );
};
